import type { Container } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";
import type { ModalChrome } from "@slot/game-shell";

/** The panels' motion values (seconds of the chrome runner). */
export interface PanelMotionTuning {
  /** The dim fading in under the panel, and out with it. */
  dimInSeconds: number;
  dimOutSeconds: number;
  /** The panel popping in from `popFromScale` on `backOut`, and shrinking out to `exitScale` as it fades. */
  popSeconds: number;
  popFromScale: number;
  exitSeconds: number;
  exitScale: number;
  /** The "tap anywhere" hint's fades. */
  continueFadeInSeconds: number;
  continueFadeOutSeconds: number;
}

/**
 * A card panel's motion (the title has no Spine panels): seated hidden before it shows, so no frame shows the end of the
 * last exit; on enter the dim fades in while the panel pops in; on exit the panel shrinks and fades with the dim and the
 * hint. Everything runs on the chrome runner the panel's `ModalChrome` fades on.
 */
export class PanelMotion {
  private generation = 0;

  public constructor(
    private readonly input: {
      chrome: ModalChrome;
      /** What pops: the panel's art and text. */
      panel: Container;
      runner: PresentationTaskRunner;
      tuning: PanelMotionTuning;
    },
  ) {}

  /** The entry state, before the panel shows. Idempotent. */
  public preSeat(): void {
    const { chrome, panel, tuning } = this.input;
    this.generation += 1;
    chrome.stopFade(chrome.dim);
    chrome.stopFade(chrome.continueTextRoot);
    chrome.dim.alpha = 0;
    chrome.continueTextRoot.alpha = 0;
    panel.alpha = 0;
    panel.scale.set(tuning.popFromScale, tuning.popFromScale);
  }

  /** The dim fading in and the panel popping in; resolves when both are done. */
  public async enter(): Promise<void> {
    this.preSeat();
    const { chrome, panel, runner, tuning } = this.input;
    const generation = this.generation;
    await Promise.all([
      chrome.fade(chrome.dim, 1, tuning.dimInSeconds),
      runner.tweenProgress(
        tuning.popSeconds,
        (k) => {
          if (generation !== this.generation || panel.destroyed) return;
          const scale = tuning.popFromScale + (1 - tuning.popFromScale) * k;
          panel.scale.set(scale, scale);
          panel.alpha = Math.min(1, k * 3);
        },
        undefined,
        { easing: "backOut" },
      ),
    ]);
  }

  /**
   * The panel shrinking and fading with the dim and the hint; resolves when all are done. `keepDim`: the dim stays (the
   * panel's hide takes it, under something that covers the screen by then).
   */
  public async exit(options: { keepDim?: boolean } = {}): Promise<void> {
    const { chrome, panel, runner, tuning } = this.input;
    const generation = ++this.generation;
    const fromScale = panel.scale.x;
    const fromAlpha = panel.alpha;
    await Promise.all([
      options.keepDim ? Promise.resolve() : chrome.fade(chrome.dim, 0, tuning.dimOutSeconds),
      chrome.fade(chrome.continueTextRoot, 0, tuning.continueFadeOutSeconds),
      runner.tweenProgress(
        tuning.exitSeconds,
        (k) => {
          if (generation !== this.generation || panel.destroyed) return;
          const scale = fromScale + (tuning.exitScale - fromScale) * k;
          panel.scale.set(scale, scale);
          panel.alpha = fromAlpha * (1 - k);
        },
        undefined,
        { easing: "quadIn" },
      ),
    ]);
  }

  /** The "tap anywhere" hint, from 0. */
  public async fadeInContinueText(): Promise<void> {
    const { chrome, tuning } = this.input;
    chrome.stopFade(chrome.continueTextRoot);
    chrome.continueTextRoot.alpha = 0;
    await chrome.fade(chrome.continueTextRoot, 1, tuning.continueFadeInSeconds);
  }

  /** On hide: every tween stops where it is. */
  public stop(): void {
    const { chrome } = this.input;
    this.generation += 1;
    chrome.stopFade(chrome.dim);
    chrome.stopFade(chrome.continueTextRoot);
  }
}
